import React, { useMemo } from 'react'
import { Add, DeleteOutline } from '@mui/icons-material'
import { CrmButton } from '../ui'
import { CrmInput, CrmSelect } from '../ui/CrmFormControls'
import { calculateQuotationTotals } from '../../utils/quotationCalculator'
import { PACKAGE_PRICING_MODES, createPackageKey } from '../../utils/quotationPackages'
import '../../assets/styles/QuotationPackages.scss'

const money = (currency, value) => `${currency} ${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`

export default function QuotationPackageManager({
  packages = [], setPackages, items = [], setItems, eventPax = 0, currency = '₹', gstPricingMode = 'INCLUSIVE', isLocked = false,
}) {
  const allocatedPax = useMemo(() => packages.reduce((sum, pkg) => sum + Math.max(Number(pkg.pax || 0), 0), 0), [packages])

  const updatePackage = (key, changes) => {
    setPackages?.((current) => current.map((pkg) => String(pkg.key) === String(key) ? { ...pkg, ...changes } : pkg))
  }

  const removePackage = (key) => {
    setPackages?.((current) => current.filter((pkg) => String(pkg.key) !== String(key)))
    setItems?.((current) => current.map((item) => String(item.package_key || '') === String(key) ? { ...item, package_key: null } : item))
  }

  const addPackage = () => {
    const number = packages.length + 1
    setPackages?.((current) => [...current, {
      key: createPackageKey(),
      name: `Package ${number}`,
      pax: Number(eventPax || 0) > 0 ? Math.max(Number(eventPax || 0) - allocatedPax, 1) : 1,
      pricing_mode: 'PER_PAX',
      sort_order: current.length,
    }])
  }

  return (
    <section className="quotation-package-manager">
      <div className="quotation-package-manager__header">
        <div><strong>Guest packages</strong><span>Group menu items for different guest groups. Event PAX stays operational metadata; each package decides its own pricing basis.</span></div>
        {!isLocked ? <CrmButton size="small" variant="secondary" startIcon={<Add />} onClick={addPackage}>Add package</CrmButton> : null}
      </div>
      {packages.length ? (
        <div className="quotation-package-manager__list">
          {packages.map((pkg, index) => {
            const packageItems = items.filter((item) => String(item.package_key || '') === String(pkg.key))
              .map((item) => ({ ...item, package_name: pkg.name, package_pax: pkg.pax, package_pricing_mode: pkg.pricing_mode }))
            const totals = calculateQuotationTotals({ items: packageItems, gstPricingMode })
            return (
              <div className="quotation-package-card" key={pkg.key}>
                <div className="quotation-package-card__index">{index + 1}</div>
                <CrmInput label="Package name" value={pkg.name || ''} disabled={isLocked} onChange={(event) => updatePackage(pkg.key, { name: event.target.value })} />
                <CrmInput label="Package PAX" type="number" min="1" value={pkg.pax ?? ''} disabled={isLocked} onChange={(event) => updatePackage(pkg.key, { pax: Math.max(Number(event.target.value || 1), 1) })} />
                <CrmSelect label="Pricing basis" value={pkg.pricing_mode || 'ITEM_QUANTITY'} disabled={isLocked} onChange={(event) => updatePackage(pkg.key, { pricing_mode: event.target.value })}>
                  {PACKAGE_PRICING_MODES.map((mode) => <option key={mode.value} value={mode.value}>{mode.label}</option>)}
                </CrmSelect>
                <div className="quotation-package-card__summary"><span>{packageItems.length} item{packageItems.length === 1 ? '' : 's'}</span><strong>{money(currency, totals.grandTotal)}</strong></div>
                {!isLocked ? <CrmButton iconOnly variant="tertiary" aria-label={`Remove ${pkg.name || 'package'}`} onClick={() => removePackage(pkg.key)}><DeleteOutline /></CrmButton> : null}
              </div>
            )
          })}
        </div>
      ) : <div className="quotation-package-manager__empty">No packages yet. Products can remain ungrouped, or add packages such as “Standard Menu — 50 PAX” and “VIP Menu — 50 PAX”.</div>}
      {packages.length ? <div className={`quotation-package-manager__allocation ${Number(eventPax || 0) && allocatedPax !== Number(eventPax) ? 'is-warning' : ''}`}><span>Package PAX allocation</span><strong>{allocatedPax}{eventPax ? ` / ${eventPax} event PAX` : ''}</strong><small>Packages may intentionally cover only part of the event; this is informational, not a hard validation.</small></div> : null}
    </section>
  )
}
